/** /mobile_preview/ — the payload a mobile client would receive for a catalog entity,
 *  optionally with the editor's unsaved draft applied.
 *
 * The response `payload` is the real wire format the apps consume, not a rendering of
 * it: the console draws it with a port of the Android UI (see
 * `src/components/MobilePreview/`). Nothing is written server-side — the draft goes
 * through the same funnel a PATCH would use, inside a transaction that is always rolled
 * back.
 *
 * Gated by the entity's `_view` permission; `_manage` as well when a draft is supplied.
 *
 * Hand-authored wrapper (console convention: never depend on generated types for
 * feature modules).
 */
import { apiPost, ConsoleApiError } from './client';

/** Entities the endpoint can render. */
export const PREVIEW_ENTITIES = ['activity', 'itinerary', 'tour'] as const;
export type PreviewEntity = (typeof PREVIEW_ENTITIES)[number];

/** client-sync type of the returned payload — picks the renderer's screen. */
export type PreviewApiType = 'activity' | 'itinerary' | 'tour_template';

export interface MobilePreviewRequest {
  entity: PreviewEntity;
  /** The saved record's id. Previewing a never-saved record is out of scope. */
  id: number;
  /** The same body the editor would PATCH (a tour). Omit to preview saved state. Not with
   *  `changes`. */
  draft?: Record<string, unknown>;
  /** An activity's or an itinerary's record-editor draft as a changeset — the `changes` of
   *  its `POST …/changeset/` (O4), applied by that changeset's own planner and writer.
   *  Absent or empty previews saved state. */
  changes?: readonly object[];
  /** With `changes`: the changeset's `options`. */
  options?: Record<string, unknown>;
  /** Pin the API spec version; defaults to the latest cacheable one. */
  spec_version?: string;
}

export interface MobilePreviewWarning {
  /** `dropped_from_draft` — a draft key the payload does not carry was ignored.
   *  `schema_refused` — the payload violates the API schema for this spec version;
   *  it is still returned, because seeing it is the point. Left as a bare `string`
   *  so a new server-side code does not break the build. */
  code: string;
  detail: string;
}

/** The record genuinely has no mobile payload — a tour with no itinerary scaffolded
 *  yet, for instance. Nothing failed and retrying will not help, so the pane explains
 *  it rather than painting a red error over an ordinary state. */
export function isNotPreviewable(e: unknown): boolean {
  return e instanceof ConsoleApiError && e.status === 400 && e.body?.code === 'not_previewable';
}

/** A render of a catalog record's changes that needs a question answered first (O4, O11):
 *  400 `required_choice` — the server raised a question the client had not seen yet (the
 *  changeset preview's debounce had not asked it). `option`: the question's key from its
 *  error path (`options.<key>`), null when none names it; `label`: the question as the
 *  refusal names it ("Re-key import matching: choose 'rekey' or 'cancel'." → "Re-key import
 *  matching"). Null for anything else. */
export function requiredChoiceOf(e: unknown): { option: string | null; label: string | null } | null {
  if (!(e instanceof ConsoleApiError) || e.status !== 400) return null;
  const body = e.body as { code?: unknown; detail?: unknown; errors?: unknown } | undefined;
  const errors = body?.errors && typeof body.errors === 'object' && !Array.isArray(body.errors) ? (body.errors as Record<string, unknown>) : {};
  const keyed = Object.entries(errors).find(
    ([, list]) => Array.isArray(list) && list.some((x) => (x as { code?: unknown } | null)?.code === 'required_choice'),
  );
  if (body?.code !== 'required_choice' && !keyed) return null;
  const path = keyed?.[0] ?? null;
  const option = path?.startsWith('options.') || path?.startsWith('events.') ? path.slice(path.indexOf('.') + 1) : null;
  const message =
    (keyed && Array.isArray(keyed[1]) ? (keyed[1] as { message?: unknown }[]).find((x) => typeof x?.message === 'string')?.message : null) ??
    body?.detail;
  const label = typeof message === 'string' ? message.split(':')[0]?.trim() || null : null;
  return { option, label };
}

/** The record is locked by a concurrent save. Retryable as-is, so the pane offers
 *  "try again" instead of a failure. Not a type predicate, matching `isStatementTimeout`. */
export function isPreviewLocked(e: unknown): boolean {
  return e instanceof ConsoleApiError && e.status === 409 && e.body?.code === 'preview_locked';
}

export interface MobilePreviewResult {
  api_type: PreviewApiType;
  spec_version: string;
  /** `saved` when no draft was applied — note a draft of only non-content keys
   *  (e.g. `expected_updated_ts`) still reads as `saved`. */
  source: 'saved' | 'draft';
  /** Exactly what a mobile client receives. Shape depends on `api_type`. */
  payload: Record<string, unknown>;
  warnings: MobilePreviewWarning[];
  meta: {
    tour_operator?: string | null;
    elapsed_ms?: number;
    [k: string]: unknown;
  };
}

/** A render can walk a whole tour subtree; the default 30s axios budget is tight. */
const RENDER_TIMEOUT_MS = 60_000;

export function renderMobilePreview(body: MobilePreviewRequest): Promise<MobilePreviewResult> {
  return apiPost<MobilePreviewResult>('/mobile_preview/', body, {
    timeout: RENDER_TIMEOUT_MS,
  });
}
